import React, { useEffect, useRef, useState } from "react";

const LS_KEY = "margin_order_confirm_skip";

export function getOrderConfirmSkip() {
  return localStorage.getItem(LS_KEY) === "true";
}

export default function MarginOrderConfirmModal({
  open,
  onClose,
  onConfirm,
  side,
  orderType,
  pair,
  price,
  stopPrice,
  amount,
  total,
  leverage,
  baseCurrency,
  quoteCurrency,
  amountCurrency,
  totalCurrency,
}) {
  const [skipNext, setSkipNext] = useState(false);
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onCloseRef.current?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]); // onClose kept in ref — not a dependency, so checkbox state is safe

  if (!open) return null;

  const isBuy  = String(side).toUpperCase() === "BUY";
  const sideColor = isBuy ? "#01bc8d" : "#e45561";
  const sideLabel = isBuy ? "Buy" : "Sell";

  const handleConfirm = () => {
    if (skipNext) localStorage.setItem(LS_KEY, "true");
    onConfirm?.();
  };

  const rows = [
    { label: "Order Type",    value: orderType  || "—" },
    stopPrice
      ? { label: "Stop Price",  value: `${stopPrice} ${quoteCurrency}` }
      : null,
    { label: "Price",
      value: price ? `${price} ${quoteCurrency}` : "Market" },
    { label: "Amount",        value: `${amount || "—"} ${amountCurrency || baseCurrency}` },
    total
      ? { label: "Total",     value: `${total} ${totalCurrency || quoteCurrency}` }
      : null,
    { label: "Leverage",      value: leverage ? `${leverage}x` : "—" },
  ].filter(Boolean);

  return (
    <div className="agce-margin-lvg-modal-root" role="presentation">
      <div className="agce-margin-lvg-modal-backdrop" onClick={onClose} aria-hidden />
      <div
        className="agce-margin-lvg-modal-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="agce-order-confirm-title"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 400 }}
      >
        {/* Header */}
        <div className="agce-margin-lvg-modal-head">
          <h2 id="agce-order-confirm-title" className="agce-margin-lvg-modal-title">
            Order Confirmation
          </h2>
          <button type="button" className="agce-margin-lvg-modal-close" onClick={onClose} aria-label="Close">
            <i className="ri-close-line ri-lg" aria-hidden />
          </button>
        </div>

        {/* Body */}
        <div className="agce-margin-lvg-modal-body">
          {/* Pair + side badge */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <span style={{ fontWeight: 700, fontSize: 16 }}>{pair}</span>
            <span style={{
              background: sideColor,
              color: "#fff",
              fontWeight: 600,
              fontSize: 12,
              padding: "3px 14px",
              borderRadius: 4,
            }}>
              {sideLabel}
            </span>
          </div>

          {/* Order detail rows */}
          <dl className="agce-margin-lvg-stats">
            {rows.map(({ label, value }) => (
              <div key={label} className="agce-margin-lvg-stat">
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>

          {/* Don't show again */}
          <label style={{
            display: "flex", alignItems: "center", gap: 8,
            marginTop: 16, fontSize: 12, cursor: "pointer", userSelect: "none",
          }}>
            <input
              type="checkbox"
              checked={skipNext}
              onChange={(e) => setSkipNext(e.target.checked)}
              style={{ width: 14, height: 14, cursor: "pointer" }}
            />
            Don't show confirmation for future orders
          </label>
        </div>

        {/* Footer */}
        <div className="agce-margin-lvg-modal-foot">
          <button type="button" className="agce-margin-lvg-btn agce-margin-lvg-btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="agce-margin-lvg-btn agce-margin-lvg-btn--primary"
            style={{ background: sideColor, borderColor: sideColor }}
            onClick={handleConfirm}
          >
            Confirm {sideLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
